/**
 * useTelegramCall — стейт-машина 1:1 аудиозвонков Telegram в браузере менеджера
 * (DEV-879 / DEV-924, ADR-0030). В отличие от useWhatsAppCall (звонок идёт на
 * телефоне, браузер лишь наблюдает), здесь браузер — полноценная нога WebRTC:
 * сервер выступает offerer'ом, браузер отвечает SDP-answer'ом и триклит ICE.
 * Аудио форвардится Opus'ом без транскодинга через bridge на бэкенде.
 *
 * Состояние — модульный синглтон (как в useWhatsAppCall): один активный звонок
 * на аккаунт, оверлей и кнопка звонка читают одно и то же состояние.
 *
 * Сервер шлёт события звонка во все вкладки владельца (BroadcastToOwner). Ведёт
 * звонок только вкладка, где его начали или приняли (ownCall), — как acceptedHere
 * у useWabaCall: остальные не отвечают на offer и гасят баннер входящего (DEV-2684).
 * Вкладка шлёт свой legId в INITIATE/ACCEPT/SDP/ICE/HANGUP, сервер возвращает leg_id
 * звонка в offer, ICE и STATE: чужой leg_id — звонок ведёт другое устройство
 * менеджера (телефон, другая вкладка), даже если «Принять» нажали и здесь (DEV-2691).
 *
 * Сигналинг ходит через единый WS мессенджеров: команды браузер→сервер уходят
 * через sendWs, события сервер→браузер маршрутизируются useMessengerWs в
 * handle*-методы ниже. Коды — из internal/telegram/calls/signaling.go.
 */
import { ref, computed, readonly } from 'vue'
import { sendWs } from '@/features/messenger/composables/messengerWsBus'

// --- коды сигналинга (browser → server), см. signaling.go ---
const CMD_INITIATE = 'TELEGRAM_CALL_INITIATE'
const CMD_SDP_ANSWER = 'TELEGRAM_CALL_SDP_ANSWER'
const CMD_ICE = 'TELEGRAM_CALL_ICE'
const CMD_HANGUP = 'TELEGRAM_CALL_HANGUP'
const CMD_ACCEPT = 'TELEGRAM_CALL_ACCEPT'
const CMD_DECLINE = 'TELEGRAM_CALL_DECLINE'
const CMD_SYNC = 'TELEGRAM_CALL_SYNC'

/** Звонки Telegram включены. Флаг сборки отключает фичу до валидации на проде (DEV-924). */
export const TELEGRAM_CALLS_ENABLED = import.meta.env.VITE_TELEGRAM_CALLS !== 'false'

// Состояния, которые сервер шлёт после «Принять» (AcceptIncoming): входящий приняли.
const ACCEPTED_STATES = new Set(['browser_connecting', 'active'])

// Сервер звонит в CRM 60 с (incomingRingTimeout). Баннер, чьё завершение не дошло
// (сокет спал), гаснет сам чуть позже (DEV-2684).
export const RING_TIMEOUT_MS = 65_000

export interface WsTelegramIncomingPayload {
  call_id: string
  peer_tg_user_id: number
  peer_name?: string
}

// Публичный STUN — браузер за NAT добирает srflx-кандидата. У сервера публичный
// IP (NAT1To1 + UDPMux), поэтому TURN не нужен (relay-fallback отключён, см. гейт).
const ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }]

/**
 * TgCallState — UI-состояния звонка. Надмножество persisted-состояний бэкенда:
 * initiating/connecting/ringing/active/ended приходят в TELEGRAM_CALL_STATE,
 * incoming — локальное состояние ожидания accept/decline после INCOMING.
 */
export type TgCallState =
  | 'idle'
  | 'initiating' // исходящий: INITIATE отправлен, ждём SDP-offer
  | 'connecting' // WebRTC-handshake (browser_connecting)
  | 'ringing' // у абонента звонит
  | 'active' // разговор идёт
  | 'incoming' // входящий: ждём решения менеджера
  | 'ended'

// маппинг persisted/UI-состояний бэкенда → локальные
function mapServerState(s: string): TgCallState {
  switch (s) {
    case 'initiating':
      return 'initiating'
    case 'browser_connecting':
    case 'connecting':
    case 'requested':
    case 'waiting':
      return 'connecting'
    case 'ringing':
      return 'ringing'
    case 'active':
      return 'active'
    case 'ended':
    case 'failed':
    case 'discarded':
      return 'ended'
    default:
      return 'connecting'
  }
}

// --- модульный синглтон состояния ---
const callState = ref<TgCallState>('idle')
const callId = ref<string | null>(null)
const peerKey = ref<string | null>(null)
const peerName = ref<string>('')
const direction = ref<'outgoing' | 'incoming' | null>(null)
const clientId = ref<string | null>(null)
const isMuted = ref(false)
const durationSeconds = ref(0)
const callError = ref<string>('')
const discardReason = ref<string>('')
// callId последнего завершённого звонка — для пост-колл карточки (DEV-925).
const endedCallId = ref<string | null>(null)
// тикает при TELEGRAM_CALL_ANALYSIS_COMPLETED — карточка перечитывает детали.
const analysisReadyAt = ref<number>(0)
// Звонок начат или принят в этой вкладке — только она поднимает WebRTC-ногу.
let ownCall = false
// DEV-2691. Устройство (вкладка) в сигналинге звонка.
// randomUUID есть только в secure context: на http://<IP> модуль не должен падать при импорте.
const legId =
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
/** Событие звонка с leg_id другого устройства. Без leg_id (старый сервер) — своё. */
const foreignLeg = (data: { leg_id?: string }) => !!data.leg_id && data.leg_id !== legId
// DEV-2684. Окно звонка смонтировано — вкладка спрашивает звонящие при подключении сокета.
let syncEnabled = false
// Баннер, висевший при запросе синхронизации: нет его в ответе — звонок кончился.
let syncFor: string | null = null
let ringTimer: ReturnType<typeof setTimeout> | null = null

// --- нереактивные ресурсы WebRTC ---
let pc: RTCPeerConnection | null = null
let localStream: MediaStream | null = null
let remoteAudio: HTMLAudioElement | null = null
const pendingRemoteIce: RTCIceCandidateInit[] = []
let durationTimer: ReturnType<typeof setInterval> | null = null
let answeredAt = 0

function startDurationTimer() {
  stopDurationTimer()
  answeredAt = Date.now()
  durationTimer = setInterval(() => {
    durationSeconds.value = Math.floor((Date.now() - answeredAt) / 1000)
  }, 1000)
}

function stopDurationTimer() {
  if (durationTimer) {
    clearInterval(durationTimer)
    durationTimer = null
  }
}

function attachRemoteStream(stream: MediaStream) {
  if (!remoteAudio) {
    remoteAudio = new Audio()
    remoteAudio.autoplay = true
    // элемент не в DOM — воспроизведение аудио этого не требует.
  }
  remoteAudio.srcObject = stream
  void remoteAudio.play().catch(() => {
    // autoplay-политика: play() инициирован кликом (звонок/accept), но на всякий
    // случай глушим reject — звук появится при следующем взаимодействии.
  })
}

async function ensureLocalStream(): Promise<MediaStream> {
  if (!localStream) {
    localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
  }
  return localStream
}

async function setupPeer(id: string): Promise<RTCPeerConnection> {
  const stream = await ensureLocalStream()

  pc = new RTCPeerConnection({ iceServers: ICE_SERVERS })

  for (const track of stream.getAudioTracks()) {
    track.enabled = !isMuted.value
    pc.addTrack(track, stream)
  }

  pc.ontrack = (ev) => {
    attachRemoteStream(ev.streams[0] ?? new MediaStream([ev.track]))
  }

  pc.onicecandidate = (ev) => {
    if (ev.candidate) {
      sendWs(CMD_ICE, {
        call_id: id,
        candidate: ev.candidate.candidate,
        sdp_mid: ev.candidate.sdpMid ?? '',
        sdp_mline_index: ev.candidate.sdpMLineIndex ?? null,
        leg_id: legId,
      })
    }
  }

  pc.onconnectionstatechange = () => {
    // Медиапуть браузер↔сервер умер (ICE не сошёлся) → звонок мёртв. Кладём
    // трубку САМИ: не ждём TELEGRAM_CALL_ENDED от сервера (он может не дойти при
    // обрыве WS — тогда оверлей зависал бы с «соединение прервано», DEV-986).
    if (pc && pc.connectionState === 'failed' && callState.value !== 'ended') {
      callError.value = 'connection_failed'
      if (callId.value) sendWs(CMD_HANGUP, { call_id: callId.value, leg_id: legId })
      endedCallId.value = callId.value
      callState.value = 'ended'
      teardownPeer()
    }
  }

  return pc
}

function teardownPeer() {
  stopDurationTimer()
  if (pc) {
    pc.ontrack = null
    pc.onicecandidate = null
    pc.onconnectionstatechange = null
    pc.close()
    pc = null
  }
  if (localStream) {
    for (const track of localStream.getTracks()) track.stop()
    localStream = null
  }
  if (remoteAudio) {
    remoteAudio.srcObject = null
    remoteAudio = null
  }
  pendingRemoteIce.length = 0
}

function resetState() {
  if (ringTimer) clearTimeout(ringTimer)
  ringTimer = null
  ownCall = false
  callState.value = 'idle'
  callId.value = null
  peerKey.value = null
  peerName.value = ''
  direction.value = null
  clientId.value = null
  isMuted.value = false
  durationSeconds.value = 0
  callError.value = ''
  discardReason.value = ''
}

const isActive = computed(() => callState.value !== 'idle' && callState.value !== 'ended')

const formattedDuration = computed(() => {
  const mins = Math.floor(durationSeconds.value / 60)
  const secs = durationSeconds.value % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
})

export function useTelegramCall() {
  // --- действия (browser → server) ---

  /** Исходящий звонок. PC создаётся при получении SDP-offer от сервера. */
  function initiateCall(pk: string, name: string, cid?: string) {
    if (isActive.value) {
      callError.value = 'busy'
      return
    }
    resetState()
    ownCall = true
    direction.value = 'outgoing'
    peerKey.value = pk
    peerName.value = name
    clientId.value = cid ?? null
    callState.value = 'initiating'
    if (!sendWs(CMD_INITIATE, { peer_key: pk, client_id: cid ?? '', leg_id: legId })) {
      callError.value = 'ws_disconnected'
      callState.value = 'ended'
    }
  }

  /** Принять входящий: сервер после ACCEPT пришлёт SDP-offer (тот же путь). */
  function acceptIncoming() {
    if (!callId.value) return
    ownCall = true
    callError.value = ''
    callState.value = 'connecting'
    if (!sendWs(CMD_ACCEPT, { call_id: callId.value, leg_id: legId })) {
      // Сокет в обрыве — баннер остаётся, принять можно после переподключения.
      ownCall = false
      callState.value = 'incoming'
      callError.value = 'ws_disconnected'
    }
  }

  /** Отклонить входящий. */
  function declineIncoming() {
    if (callId.value) sendWs(CMD_DECLINE, { call_id: callId.value })
    endedCallId.value = callId.value
    callState.value = 'ended'
    discardReason.value = 'declined'
    teardownPeer()
  }

  /** Положить трубку (исходящий/активный). */
  function hangup() {
    if (callId.value) sendWs(CMD_HANGUP, { call_id: callId.value, leg_id: legId })
    callState.value = 'ended'
    teardownPeer()
  }

  function toggleMute() {
    isMuted.value = !isMuted.value
    if (localStream) {
      for (const track of localStream.getAudioTracks()) track.enabled = !isMuted.value
    }
  }

  /** Полный сброс к idle (закрытие оверлея после завершения). */
  function closeCall() {
    teardownPeer()
    resetState()
  }

  // --- обработчики событий (server → browser), вызываются из useMessengerWs ---

  async function handleSdpOffer(data: { call_id: string; sdp: string; leg_id?: string }) {
    // offer без активного намерения или звонку другой вкладки — игнорируем: иначе
    // каждая вкладка с баннером входящего ответила бы своим SDP и заняла микрофон.
    if (!ownCall || foreignLeg(data) || callState.value === 'idle' || callState.value === 'ended')
      return
    callId.value = data.call_id
    try {
      const peer = await setupPeer(data.call_id)
      await peer.setRemoteDescription({ type: 'offer', sdp: data.sdp })

      // сливаем ICE, накопленные до установки remoteDescription.
      for (const cand of pendingRemoteIce.splice(0)) {
        await peer.addIceCandidate(cand).catch(() => {})
      }

      const answer = await peer.createAnswer()
      await peer.setLocalDescription(answer)
      sendWs(CMD_SDP_ANSWER, { call_id: data.call_id, sdp: answer.sdp, leg_id: legId })
    } catch (e) {
      callError.value = e instanceof DOMException ? 'mic_denied' : 'webrtc_error'
      hangup()
    }
  }

  async function handleRemoteIce(data: {
    call_id: string
    candidate: string
    sdp_mid?: string
    sdp_mline_index?: number | null
    leg_id?: string
  }) {
    if (!ownCall || foreignLeg(data)) return
    const init: RTCIceCandidateInit = {
      candidate: data.candidate,
      sdpMid: data.sdp_mid ?? null,
      sdpMLineIndex: data.sdp_mline_index ?? null,
    }
    if (pc && pc.remoteDescription) {
      await pc.addIceCandidate(init).catch(() => {})
    } else {
      pendingRemoteIce.push(init)
    }
  }

  function handleState(data: {
    call_id: string
    state: string
    peer_name?: string
    leg_id?: string
  }) {
    const next = mapServerState(data.state)
    // «Принять» нажали и здесь, и на другом устройстве — звонок ведёт то, чей
    // leg_id в событии: окно здесь гаснет, HANGUP не шлём (DEV-2691).
    if (ownCall && foreignLeg(data)) {
      if (data.call_id === callId.value) {
        teardownPeer()
        resetState()
      }
      return
    }
    if (!ownCall) {
      // Звонок другой вкладки здесь не зеркалим. Входящий до принятия живёт в
      // 'incoming' (кнопки accept/decline): промежуточный STATE ('ringing'/'connecting')
      // его не перетирает на исходящий лейаут «Вызов…»+Отмена (DEV-990), терминальный
      // закрывает неотвеченный/отменённый, а принятый в другой вкладке гасит баннер
      // и мелодию (DEV-2684).
      if (callState.value !== 'incoming' || data.call_id !== callId.value) return
      if (next === 'ended') callState.value = 'ended'
      else if (ACCEPTED_STATES.has(data.state)) resetState()
      return
    }
    callId.value = data.call_id
    if (data.peer_name) peerName.value = data.peer_name
    if (next === 'active' && callState.value !== 'active') {
      startDurationTimer()
    }
    // не откатываем уже завершённый звонок запоздалым STATE.
    if (callState.value !== 'ended') callState.value = next
  }

  function handleEnded(data: {
    call_id: string
    state: string
    discard_reason?: string
    duration_sec?: number
  }) {
    if (callId.value && data.call_id !== callId.value) return
    endedCallId.value = data.call_id
    discardReason.value = data.discard_reason ?? ''
    if (typeof data.duration_sec === 'number') durationSeconds.value = data.duration_sec
    callState.value = 'ended'
    teardownPeer()
  }

  function handleIncoming(data: WsTelegramIncomingPayload) {
    // Тот же звонок ещё раз (ответ на синхронизацию, переподключение) — окно уже есть;
    // ветка «занят» ниже отклонила бы его самого.
    if (data.call_id === callId.value) return
    if (ownCall && isActive.value) {
      // уже в звонке — сразу отклоняем (сервер тоже вернёт busy).
      sendWs(CMD_DECLINE, { call_id: data.call_id })
      return
    }
    // Чужой баннер прежнего входящего мёртв: у аккаунта один звонок, при звонящем
    // сервер новый INCOMING не шлёт. Заменяем, а не отклоняем настоящий звонок.
    resetState()
    direction.value = 'incoming'
    callId.value = data.call_id
    peerName.value = data.peer_name ?? ''
    callState.value = 'incoming'
    ringTimer = setTimeout(() => {
      if (!ownCall && callState.value === 'incoming' && callId.value === data.call_id) resetState()
    }, RING_TIMEOUT_MS)
  }

  function setSyncEnabled(enabled: boolean) {
    syncEnabled = enabled
  }

  /**
   * DEV-2684. Вкладка, открытая уже во время звонка (по ссылке из уведомления),
   * INCOMING не застала — спрашиваем звонящие сами. Зовётся при каждом подключении
   * сокета; ответ — TELEGRAM_CALL_RINGING.
   */
  function syncRinging() {
    if (!syncEnabled) return
    syncFor = !ownCall && callState.value === 'incoming' ? callId.value : null
    sendWs(CMD_SYNC, {})
  }

  /**
   * Ответ на синхронизацию: каждый звонящий — как обычное входящее (дубли отсекаются).
   * Баннер, висевший при запросе и не вошедший в ответ, гаснет: завершение звонка
   * ушло в мёртвый сокет. Пришедший после запроса INCOMING ответ не трогает.
   */
  function handleRinging(data: { calls?: WsTelegramIncomingPayload[] } | null) {
    const calls = data?.calls ?? []
    if (
      syncFor &&
      !ownCall &&
      callState.value === 'incoming' &&
      callId.value === syncFor &&
      !calls.some((c) => c.call_id === syncFor)
    ) {
      resetState()
    }
    syncFor = null
    for (const call of calls) handleIncoming(call)
  }

  function handleAnalysisCompleted(data: { call_id: string }) {
    if (data.call_id) {
      endedCallId.value = data.call_id
      analysisReadyAt.value = Date.now()
    }
  }

  return {
    // состояние (readonly наружу)
    callState: readonly(callState),
    callId: readonly(callId),
    peerKey: readonly(peerKey),
    peerName: readonly(peerName),
    direction: readonly(direction),
    isMuted: readonly(isMuted),
    durationSeconds: readonly(durationSeconds),
    callError: readonly(callError),
    discardReason: readonly(discardReason),
    endedCallId: readonly(endedCallId),
    analysisReadyAt: readonly(analysisReadyAt),
    isActive,
    formattedDuration,
    // действия
    initiateCall,
    acceptIncoming,
    declineIncoming,
    hangup,
    toggleMute,
    closeCall,
    setSyncEnabled,
    syncRinging,
    // обработчики WS
    handleRinging,
    handleSdpOffer,
    handleRemoteIce,
    handleState,
    handleEnded,
    handleIncoming,
    handleAnalysisCompleted,
  }
}
